import { useEffect, useState } from 'react';
import { apiUrl } from '../../../api/apiUrl';
import http from '../../../api/axios';
import DataTable from '../../../components/table/DataTable';
import { DocumentArrowDownIcon, DocumentArrowUpIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../../components/button/Button';
import { toast } from 'react-toastify';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { CodeBookItem } from '../../../types/CodeBookItem';
import { paths } from '../../RouterContainer';
import { columns } from '../../../components/columns/Company/CodeBookItemColumns';
import { useTranslation } from '../../../context/translationContext';
import { TableColumn } from '../../../components/table/DataTable.types';
import { VatCode } from '../../../types/VatCode';
import { DirectionDoc } from '../../../types/Invoice';
import { Loader } from '../../../components/loader/Loader';
import { dateSubmitFormat } from '../../../types/core/date.types';
import { format } from 'date-fns';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import { onDownloadItem } from '../../../utils/functions/file.functions';
import { useLoading } from '../../../context/loadingContext';
import { Modal } from '../../../components/modal/modal';
import { Datepicker } from '../../../components/datepicker/Datepicker';
import { FileUpload } from '../../../components/files/FileUpload';
/**
 * Page component represents code book items for current company
 */
export const CodeBookItemsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<CodeBookItem[] | null>(null);
  const [vatCodes, setVatCodes] = useState<VatCode[] | undefined>();
  const { company } = useCurrentCompany();
  const { startLoading, stopLoading, loading } = useLoading();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [openImportModal, setOpenImportModal] = useState<boolean>(false);
  const [validFrom, setValidFrom] = useState<Date | null>(new Date() ?? null);
  const [xlsxFile, setXlsxFile] = useState<File[]>([]);
  useEffect(() => {
    setBreadcrumbs([{ path: `${paths.CODE_BOOK_ITEMS}`, name: 'navigation.code-book-items' }]);
  }, []);

  /**
   * Loads company code book items
   */
  const fetchData = async () => {
    if (!company) return;
    const response = await http.apiGet({
      url: `${apiUrl.CODE_BOOK_ITEM_LIST_WITH_PRICES}?parentId=${company.id}&validToDate=${format(
        new Date(),
        dateSubmitFormat,
      )}`,
    });
    setData(response?.data);
  };

  /**
   * Loads vat codes
   */
  const fetchVatCodes = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.VAT_CODE_GET_LIST_FILTERED}?direction=${DirectionDoc.OUTPUT_DOC}`,
    });
    if (response?.data) {
      setVatCodes(response?.data);
    }
  };

  useEffect(() => {
    fetchVatCodes();
  }, []);

  useEffect(() => {
    setData(null);
    fetchData();
  }, [company]);

  /**
   * Removes code book item
   * @param id
   * @returns
   */
  const onRemoveItem = async (id: string | number) => {
    if (id === null) return;
    const response = await http.apiDelete({
      url: `${apiUrl.CODE_BOOK_ITEM_DELETE}?id=${id}`,
    });
    if (response?.status === 204) {
      toast.success('Success!');
      fetchData();
    }
  };

  /**
   * Redirects to company edit page
   * @param id of company
   */
  const onEditItem = (id: string | number) => {
    if (id === null) return;
    navigate(`/${paths.CODE_BOOK_ITEMS}/edit/${id}`);
  };

  /**
   * Downloads template for importing from excel
   */
  const onDownloadTemplate = () => {
    startLoading();
    onDownloadItem(`${apiUrl.CODE_BOOK_ITEM_DOWNLOAD_TEMPLATE}`, 'CodeBookItemTemplate.xlsx').then(
      () => stopLoading(),
    );
  };

  /**
   * Opens import from excel modal
   */
  const handleImportFromExcel = () => {
    setOpenImportModal(true);
  };

  /**
   * Submits import from excel
   * @returns
   */
  const submitImportFromExcel = async () => {
    if (!validFrom || !xlsxFile[0] || !company) return;
    try {
      startLoading();
      const apiPostFilesUrl = `${apiUrl.CODE_BOOK_ITEM_IMPORT_FROM_EXCEL}?validFrom=${format(
        validFrom,
        dateSubmitFormat,
      )}&companyId=${company?.id}`;
      const formData = new FormData();
      // can upload only 1 file
      formData.append('file', xlsxFile[0]);
      const response = await http.apiPostFiles({
        url: apiPostFilesUrl,
        data: formData,
      });
      if (response?.status === 200) {
        stopLoading();
        toast.success('Success!');
        fetchData();
        handleCloseImportModal();
      } else {
        stopLoading();
        handleCloseImportModal();
        toast.error('error');
      }
    } catch (err) {
      stopLoading();
      handleCloseImportModal();
      toast.error('error');
    }
  };

  /**
   * Closes import from excel modal
   */
  const handleCloseImportModal = () => {
    setOpenImportModal(false);
    setXlsxFile([]);
  };
  if (!vatCodes) return <Loader />;
  return (
    <>
      <div className="flex justify-between">
        <h1 className="text-3xl font-bold">{t('page.code-book-item.headline')}</h1>
        <div>
          <Button
            className="mr-3"
            onClick={handleImportFromExcel}
            title={t('page.code-book-item.btn-import-from-excel')}
            showLoading
            icon={<DocumentArrowUpIcon className="w-4 h-4 mr-1 cursor-pointer" />}
          />
          <Button
            className="mr-3"
            onClick={onDownloadTemplate}
            title={t('page.code-book-item.btn-download-template')}
            showLoading
            disabled={loading}
            icon={<DocumentArrowDownIcon className="w-4 h-4 mr-1 cursor-pointer" />}
          />
          <Link to={`/${paths.CODE_BOOK_ITEMS}/create`}>
            <Button
              title={t('general.create-new')}
              color="green"
              icon={<PlusIcon className="w-4 h-4 mr-2" />}
            />
          </Link>
        </div>
      </div>
      <DataTable
        data={data}
        // remove column "code" from table if codeBookItemsUseCode === false
        columns={
          company?.codeBookItemsUseCode
            ? columns(vatCodes)
            : columns(vatCodes).filter((x: TableColumn) => x.accessor !== 'code')
        }
        handleRemove={onRemoveItem}
        handleEdit={onEditItem}
      />
      {/* Import from excel modal */}
      <Modal
        open={openImportModal}
        title={t('page.code-book-item.import-from-excel')}
        onClose={() => setOpenImportModal(false)}
      >
        <div className="mt-3">
          <FileUpload
            showUploadButton={false}
            setSelectedFile={setXlsxFile}
            selectedFile={xlsxFile}
          />
        </div>
        <div className="w-1/2 flex items-center">
          <span className="w-32">{t('page.code-book-item.valid-from')}</span>
          <Datepicker
            onChange={(e) => setValidFrom(e)}
            placeholder={t('page.code-book-item.valid-from')}
            value={validFrom}
            className="relative border my-2.5 cursor-default rounded-lg bg-white py-2 h-9 pl-3 pr-5 text-left shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white"
          />
        </div>
        <Button
          title={t('general.import')}
          className="bg-orange-600 hover:bg-orange-900 mr-3"
          onClick={submitImportFromExcel}
          disabled={loading}
        />
      </Modal>
    </>
  );
};
